/*
 * ConfirmDialog: confirm a consequential action. With `confirmText`, the person types it (e.g. the
 * hostname) before the button unlocks: use that for anything destructive or hard to undo.
 *   <ConfirmDialog
 *     open={open} onOpenChange={setOpen} tone="danger"
 *     title={t('servers:cancel.title')} description={…}
 *     confirmLabel={t('servers:cancel.confirm')} confirmText={server.hostname}
 *     onConfirm={() => cancel.mutateAsync(server.id)}     // the dialog shows loading and closes on success
 *   />
 */
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from './Button'
import { Dialog } from './Dialog'
import { Field } from './Field'
import { Input } from './Input'

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel,
  cancelLabel,
  tone = 'danger',
  confirmText,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  confirmLabel: ReactNode
  cancelLabel?: ReactNode
  tone?: 'danger' | 'primary'
  /** when set, the person must type exactly this to confirm */
  confirmText?: string
  onConfirm: () => unknown | Promise<unknown>
}) {
  const { t } = useTranslation('common')
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const locked = confirmText ? typed.trim() !== confirmText : false

  const change = (o: boolean) => {
    if (busy) return
    if (!o) setTyped('')
    onOpenChange(o)
  }
  const confirm = async () => {
    setBusy(true)
    try {
      await onConfirm()
      setTyped('')
      onOpenChange(false)
    } catch {
      /* the caller shows the error (toast or inline); keep the dialog open */
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={change}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => change(false)} disabled={busy}>
            {cancelLabel ?? t('actions.cancel')}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={confirm}
            disabled={locked}
            loading={busy}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {children}
        {confirmText ? (
          <Field
            label={
              <span>
                {t('ui.confirm.typeToConfirm')}{' '}
                <bdi dir="ltr" className="rounded-md bg-control px-1.5 py-0.5 font-mono text-13 text-ink">
                  {confirmText}
                </bdi>
              </span>
            }
          >
            <Input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              mono
              autoComplete="off"
              spellCheck={false}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !locked) void confirm()
              }}
            />
          </Field>
        ) : null}
      </div>
    </Dialog>
  )
}
